import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Type, X } from 'lucide-react';
import { IconSend2, IconSparkles2 } from '@tabler/icons-react';
import OryaThinkingStatus from '../../documentation/components/OryaThinkingStatus';
import { formatDuration } from '../utils/boardChatAttachmentUtils';
import { aiWriteComposer } from '../services/boardChatService';
import { useComposerVoiceRecorder, VoiceMicButton } from './ComposerVoiceRecorder';

const THINKING_STEPS = [
  { text: 'Analisando pedido…', duration: 2000 },
  { text: 'Refinando a escrita…', duration: 2500 },
  { text: 'Gerando resposta…', duration: null },
];

const TEXTAREA_MIN_PX = 28;
const TEXTAREA_MAX_PX = 120;

function msgId() {
  return `bch-ai-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * Popover "Escrever com IA" (Orya / MiMo Token Plan).
 * Chat no painel: thinking → resposta Orya → input continua abaixo.
 */
export function BoardChatAiWriteMenu({
  open = false,
  onOpenChange,
  tipProps,
  hideTip,
  onOpen,
  draft = '',
  onApply,
  applyLabel = 'Usar no campo de mensagem',
  hideTrigger = false,
}) {
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const feedEndRef = useRef(null);
  const [prompt, setPrompt] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const voiceRecorder = useComposerVoiceRecorder({
    onError: (msg) => setError(msg || 'Falha no microfone'),
  });

  const hasThread = messages.length > 0 || loading;
  const voiceActive = voiceRecorder.recording || voiceRecorder.busy;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      onOpenChange?.(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') onOpenChange?.(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => inputRef.current?.focus(), 40);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setPrompt('');
      setError('');
      setLoading(false);
      setMessages([]);
      voiceRecorder.cancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só ao fechar o painel
  }, [open]);

  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.lineHeight = '28px';
    const scroll = el.scrollHeight;
    if (scroll > TEXTAREA_MIN_PX + 1) {
      el.style.lineHeight = '17px';
      el.style.height = 'auto';
      const h = Math.min(Math.max(el.scrollHeight, TEXTAREA_MIN_PX), TEXTAREA_MAX_PX);
      el.style.height = `${h}px`;
    } else {
      el.style.height = `${TEXTAREA_MIN_PX}px`;
    }
  }, [prompt, open]);

  useEffect(() => {
    if (!open || !hasThread) return;
    feedEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [open, hasThread, messages, loading, error, voiceActive]);

  const handleToggle = () => {
    hideTip?.();
    const next = !open;
    if (next) onOpen?.();
    onOpenChange?.(next);
  };

  const canSend = Boolean(String(prompt || '').trim()) && !loading && !voiceRecorder.busy;

  const handleTranscribe = async () => {
    setError('');
    try {
      if (!voiceRecorder.speechSupported) {
        await voiceRecorder.cancel();
        setError('Não foi possível obter o texto desta gravação.');
        return;
      }
      const text = String((await voiceRecorder.finishTranscript()) || '').trim();
      if (!text) {
        setError('Não foi possível obter o texto desta gravação.');
        return;
      }
      setPrompt((prev) => (prev ? `${prev.trim()} ${text}` : text));
    } catch (e) {
      setError(e?.message || 'Não foi possível obter o texto desta gravação.');
    }
  };

  const handleSend = async () => {
    const text = String(prompt || '').trim();
    if (!text || loading) return;

    const history = messages
      .filter((m) => !m.isError && (m.role === 'user' || m.role === 'assistant'))
      .map((m) => ({ role: m.role, content: m.content }));

    setMessages((prev) => [...prev, { id: msgId(), role: 'user', content: text }]);
    setPrompt('');
    setLoading(true);
    setError('');

    try {
      const res = await aiWriteComposer({
        prompt: text,
        draft: history.length === 0 ? draft : undefined,
        history,
      });
      const content = String(res?.data?.content || '').trim();
      if (!content) {
        setError('A IA não retornou texto. Tente de novo.');
        return;
      }
      setMessages((prev) => [
        ...prev,
        { id: msgId(), role: 'assistant', content },
      ]);
    } catch (e) {
      setError(e?.message || 'Não foi possível gerar o texto.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bch-ai-write-wrap" ref={wrapRef}>
      {hideTrigger ? null : (
      <button
        type="button"
        className={`bch-composer-tool${open ? ' is-active' : ''}`}
        aria-label="Escrever com IA"
        aria-expanded={open}
        aria-haspopup="dialog"
        {...(tipProps ? tipProps('Escrever com IA', 'above') : {})}
        onClick={handleToggle}
      >
        <IconSparkles2 size={16} stroke={1.8} aria-hidden />
      </button>
      )}

      {open ? (
        <div
          className={`bch-ai-write-panel${hasThread ? ' bch-ai-write-panel--thread' : ''}`}
          role="dialog"
          aria-label="Escrever com IA"
          aria-modal="false"
        >
          {!hasThread ? (
            <div className="bch-ai-write-hero">
              <span className="bch-ai-write-hero-icon" aria-hidden>
                <IconSparkles2 size={18} stroke={1.6} />
              </span>
              <h3 className="bch-ai-write-title">Como posso melhorar a sua escrita?</h3>
              <p className="bch-ai-write-desc">
                Posso refinar sua escrita, corrigir a gramática e muito mais.
              </p>
            </div>
          ) : (
            <div className="bch-ai-write-head">
              <span className="bch-ai-write-head-icon" aria-hidden>
                <IconSparkles2 size={16} stroke={1.7} />
              </span>
              <span className="bch-ai-write-head-name">Orya</span>
            </div>
          )}

          {hasThread ? (
            <div className="bch-ai-write-feed" role="log" aria-label="Conversa com a Orya">
              {messages.map((m) =>
                m.role === 'user' ? (
                  <div key={m.id} className="bch-ai-write-msg bch-ai-write-msg--user">
                    <p className="bch-ai-write-msg-text">{m.content}</p>
                  </div>
                ) : (
                  <div key={m.id} className="bch-ai-write-msg bch-ai-write-msg--assistant">
                    <div className="bch-ai-write-msg-orya">
                      <span className="bch-ai-write-msg-orya-icon" aria-hidden>
                        <IconSparkles2 size={14} stroke={1.7} />
                      </span>
                      <span className="bch-ai-write-msg-orya-name">Orya</span>
                    </div>
                    <p className="bch-ai-write-msg-text">{m.content}</p>
                    {onApply ? (
                      <button
                        type="button"
                        className="bch-ai-write-use"
                        onClick={() => onApply(m.content)}
                      >
                        {applyLabel}
                      </button>
                    ) : null}
                  </div>
                )
              )}
              {loading ? (
                <div className="bch-ai-write-thinking">
                  <OryaThinkingStatus active={loading} steps={THINKING_STEPS} />
                </div>
              ) : null}
              {error ? (
                <p className="bch-ai-write-error" role="alert">
                  {error}
                </p>
              ) : null}
              <div ref={feedEndRef} />
            </div>
          ) : null}

          <div className="bch-ai-write-composer">
            {voiceActive ? (
              <div className="bch-ai-write-transcribe-pop" role="status" aria-live="polite">
                <span className={`bch-voice-pulse${voiceRecorder.busy ? ' is-busy' : ''}`} aria-hidden />
                <span className="bch-voice-timer">{formatDuration(voiceRecorder.elapsedSec)}</span>
                <span className="bch-voice-label">
                  {voiceRecorder.busy ? 'Processando...' : 'Gravando...'}
                </span>
                <button
                  type="button"
                  className="bch-voice-action bch-voice-action--primary"
                  disabled={voiceRecorder.busy}
                  onClick={handleTranscribe}
                >
                  <Type size={14} strokeWidth={1.9} />
                  Transcrever
                </button>
                <button
                  type="button"
                  className="bch-voice-cancel"
                  disabled={voiceRecorder.busy}
                  aria-label="Cancelar gravação"
                  onClick={() => voiceRecorder.cancel()}
                >
                  <X size={13} strokeWidth={2} />
                </button>
              </div>
            ) : null}

            <div className="bch-ai-write-input-wrap">
              <textarea
                ref={inputRef}
                className="bch-ai-write-input"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Peça à Orya para editar ou escrever"
                aria-label="Pedido para a Orya"
                disabled={loading || voiceRecorder.busy}
                rows={1}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
              />
              <div className="bch-ai-write-input-foot">
                <span className="bch-ai-write-mic">
                  <VoiceMicButton
                    recording={voiceActive}
                    tipProps={tipProps}
                    hideTip={hideTip}
                    onClick={() => {
                      if (loading || voiceRecorder.busy) return;
                      voiceRecorder.toggle();
                    }}
                  />
                </span>
                <button
                  type="button"
                  className={`bch-ai-write-input-btn bch-ai-write-input-btn--send${canSend ? ' is-ready' : ''}`}
                  aria-label={loading ? 'Gerando…' : 'Enviar'}
                  disabled={!canSend}
                  title={loading ? 'Gerando…' : 'Enviar'}
                  onClick={handleSend}
                >
                  <IconSend2 size={16} stroke={1.8} aria-hidden />
                </button>
              </div>
            </div>
          </div>
          {!hasThread && error ? (
            <p className="bch-ai-write-error" role="alert">
              {error}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
